import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, FileText, FolderOpen,
  HardHat, Hammer, BarChart3,
  ChevronLeft, ChevronRight, Shield, UserCog,
  Layers, Activity, KeyRound, Ruler, TrendingUp
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROLES } from '@/lib/permissions';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permissions?: string[];
  roles?: string[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} />, permissions: ['dashboard:view'] },
  { label: 'Departmental Labour-Requirement', path: '/requirements', icon: <FileText size={20} />, permissions: ['requirement:view'] },
  { label: 'Pending Approvals', path: '/approvals', icon: <Shield size={20} />, roles: [ROLES.PROJECT_MANAGER], permissions: ['requirement:manager_approve', 'requirement:manager_reject', 'actual:manager_approve', 'actual:manager_reject'] },
  { label: 'Pending Final Approvals', path: '/approvals', icon: <Shield size={20} />, roles: [ROLES.CLUSTER_HEAD], permissions: ['requirement:cluster_approve', 'requirement:cluster_reject', 'actual:cluster_approve', 'actual:cluster_reject'] },
  { label: 'Departmental Labour -Actual', path: '/actuals', icon: <Activity size={20} />, permissions: ['actual:view'] },
  { label: 'Rate Card', path: '/rate-card', icon: <FileText size={20} />, permissions: ['rate_cards:view'] },
  { label: 'Reports', path: '/reports', icon: <BarChart3 size={20} />, permissions: ['reports:view'] },
  { label: 'Labour Analytics', path: '/reports/analytics', icon: <TrendingUp size={20} />, permissions: ['reports:view'] },
];

const adminItems: NavItem[] = [
  { label: 'Users', path: '/admin/users', icon: <UserCog size={20} />, permissions: ['users:view'] },
  { label: 'Roles', path: '/admin/roles', icon: <KeyRound size={20} />, permissions: ['roles:view'] },
];

const masterItems: NavItem[] = [
  { label: 'Projects', path: '/admin/projects', icon: <FolderOpen size={20} />, permissions: ['projects:view'] },
  { label: 'Blocks', path: '/admin/blocks', icon: <Layers size={20} />, permissions: ['blocks:view'] },
  { label: 'Activities', path: '/admin/activities', icon: <Hammer size={20} />, permissions: ['activities:view'] },
  { label: 'Units', path: '/admin/units', icon: <Ruler size={20} />, permissions: ['units:view'] },
  { label: 'Worker Categories', path: '/admin/worker-categories', icon: <HardHat size={20} />, permissions: ['worker_categories:view'] },
];

interface SidebarProps {
  mobileNavOpen?: boolean;
  onCloseMobileNav?: () => void;
}

export function Sidebar({ mobileNavOpen = false, onCloseMobileNav }: SidebarProps) {
  const { user, hasAnyRole, hasAnyPermission } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const isSystemAdmin = hasAnyRole([ROLES.ORG_ADMIN]);
  const isExistingStandardUser = hasAnyRole([
    ROLES.SITE_ENGINEER,
    ROLES.PROJECT_MANAGER,
    ROLES.CLUSTER_HEAD,
    ROLES.VIEWER,
  ]);
  const hasLoadedPermissions = Array.isArray(user?.permissions) && user.permissions.length > 0;
  const canShowMainItem = (item: NavItem) => {
    // Approval menus are workflow-specific: they are never available to the
    // System Admin (ORG_ADMIN holds every permission but has no Manager ->
    // Cluster Head approval workflow), and otherwise follow the original
    // role-based visibility with a permission-key fallback for custom roles.
    if (item.roles?.length) {
      if (isSystemAdmin) return false;
      if (hasAnyRole(item.roles)) return true;
      return !!item.permissions?.length && hasLoadedPermissions && hasAnyPermission(item.permissions);
    }
    if (isSystemAdmin) return true;
    // Preserve the original menu exactly for Engineer, Manager, Cluster Head
    // and Viewer. Only their original approval-specific item uses roles.
    if (isExistingStandardUser) return !item.roles?.length;
    return !!item.permissions?.length && hasLoadedPermissions && hasAnyPermission(item.permissions);
  };

  const filteredNav = navItems.filter(canShowMainItem);
  const filteredAdmin = adminItems.filter((item) => {
    if (isSystemAdmin) return true;
    // Existing standard users never had Administration menus.
    if (isExistingStandardUser) return false;
    return !!item.permissions?.length && hasLoadedPermissions && hasAnyPermission(item.permissions);
  });
  const filteredMasters = masterItems.filter((item) => {
    if (isSystemAdmin) return true;
    if (isExistingStandardUser) return false;
    return !!item.permissions?.length && hasLoadedPermissions && hasAnyPermission(item.permissions);
  });
  const showAdmin = filteredAdmin.length > 0;
  const showMasters = filteredMasters.length > 0;

  return (
    <aside
      className={cn(
        'sidebar sticky top-0 z-40 h-screen flex-shrink-0 flex flex-col',
        'transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
        mobileNavOpen && 'sidebar--open'
      )}
    >
      <div className="relative flex flex-col flex-1 min-h-0">
        <div className="flex items-center h-16 px-4 border-b flex-shrink-0" style={{ borderColor: 'var(--sidebar-border)' }}>
          {!collapsed ? (
            <div className="flex items-center gap-2">
              <img src="/assests/SS Logo_RGB.png" alt="Departmental Labour App" className="sidebar-logo" />
              <span className="sidebar__brand font-heading font-semibold text-lg">Departmental Labour App</span>
            </div>
          ) : (
            <img src="/assests/SS Logo_RGB.png" alt="Departmental Labour App" className="sidebar-logo mx-auto" />
          )}
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-[72px] w-6 h-6 rounded-full flex items-center justify-center hover:opacity-90 transition-colors z-50 text-white"
          style={{ backgroundColor: 'var(--brand-primary)' }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {filteredNav.map(item => (
          <SidebarLink key={`${item.path}-${item.label}`} item={item} collapsed={collapsed} onCloseMobileNav={onCloseMobileNav} />
        ))}

        {showAdmin && (
          <>
            <div className={cn('my-3', collapsed ? 'mx-2' : '')} style={{ borderTop: '1px solid var(--sidebar-border)' }} />
            {!collapsed && <p className="px-3 py-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--sidebar-muted)' }}>Administration</p>}
            {filteredAdmin.map(item => (
              <SidebarLink key={`${item.path}-${item.label}`} item={item} collapsed={collapsed} onCloseMobileNav={onCloseMobileNav} />
            ))}
          </>
        )}

        {showMasters && (
          <>
            <div className={cn('my-3', collapsed ? 'mx-2' : '')} style={{ borderTop: '1px solid var(--sidebar-border)' }} />
            {!collapsed && <p className="px-3 py-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--sidebar-muted)' }}>Masters</p>}
            {filteredMasters.map(item => (
              <SidebarLink key={`${item.path}-${item.label}`} item={item} collapsed={collapsed} onCloseMobileNav={onCloseMobileNav} />
            ))}
          </>
        )}
      </nav>
      </div>

      {user && (
        <div className="p-3 flex-shrink-0" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
          <div className="flex items-center gap-2 px-1 w-full rounded-lg py-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-on-brand text-sm font-medium flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary-hover)' }}>
              {user.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="sidebar__user-name text-sm font-medium truncate">{user.name}</p>
                <p className="sidebar__user-email text-xs truncate">{user.email}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

function SidebarLink({ item, collapsed, onCloseMobileNav }: { item: NavItem; collapsed: boolean; onCloseMobileNav?: () => void }) {
  return (
    <NavLink
      to={item.path}
      onClick={onCloseMobileNav}
      className={({ isActive }) =>
        cn(
          'sidebar__nav-item flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
          collapsed && 'justify-center px-2',
          isActive && 'sidebar__nav-item--active'
        )
      }
      title={collapsed ? item.label : undefined}
    >
      {item.icon}
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}
